/**
 * The concrete engine: ProseMirror + Yjs, fully INTERNAL. Nothing in this file's PUBLIC surface
 * (see the factory return type in `index.ts`) exposes a PM or Yjs type — hosts drive the editor
 * through plain shapes only.
 *
 * Binds `ydoc.getXmlFragment("default")` — the SAME fragment name the collaboration spine uses
 * for v1 (`knowit-collaboration`), so the proven Hocuspocus persistence works unchanged for the
 * new `document-v2.<id>` room.
 */

import { HocuspocusProvider } from "@hocuspocus/provider";
import {
  baseKeymap,
  lift,
  setBlockType as pmSetBlockType,
  toggleMark,
  wrapIn,
} from "prosemirror-commands";
import { undoInputRule } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { Fragment, Node as PMNode, Slice } from "prosemirror-model";
import { liftListItem, sinkListItem, splitListItem, wrapInList } from "prosemirror-schema-list";
import { type Command, EditorState, TextSelection, type Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import {
  prosemirrorToYXmlFragment,
  redo as yRedo,
  ySyncPlugin,
  undo as yUndo,
  yUndoPlugin,
} from "y-prosemirror";
import * as Y from "yjs";
import type { Deriver } from "./deriver/Deriver";
import { NoopDeriver } from "./deriver/Deriver";
import type { EngineHandle } from "./engine/handle";
import { buildObjectNodeViews } from "./engine/objectViews";
import {
  buildInputRules,
  DOCIT_INTERNAL,
  placeholderPlugin,
  provisionalStampPlugin,
} from "./engine/plugins";
import { getLiveObject } from "./nodes/registry";
import { toTextV2 } from "./projections/markdown";
import { toContentV2 } from "./projections/pm-json";
import { ATTRIBUTION_MARK } from "./schema/marks/attribution";
import { COMMITMENT_MARK } from "./schema/marks/commitment";
import { editorSchema } from "./schema/schema";
import type {
  Attribution,
  BlockType,
  CommitmentState,
  DocRange,
  InlineFormat,
  ListType,
  ObjectActionRequest,
  ParticipantRef,
  Proposal,
  SelectionAnchor,
  SelectionInfo,
} from "./types";

/**
 * How the v2 room's socket is doing, as a plain string a host can render.
 *
 * `unauthorized` is terminal (the collaboration JWT was refused — a fresh one is needed);
 * every other value is a live state the provider moves between as it reconnects.
 */
export type CollabStatus = "connecting" | "connected" | "disconnected" | "unauthorized";

/** Live-collaboration connection params. Only plain strings cross the boundary. */
export interface CollabConfig {
  /** Collaboration websocket endpoint, e.g. `wss://kms.docit.pro/collaboration`. */
  url: string;
  documentId: string;
  /**
   * JWT for the authenticated actor (same token the v1 provider uses). A FUNCTION is read on every
   * (re)connect — a host with a short-lived, refreshed token passes one so a reconnect after a
   * refresh authenticates with the fresh token instead of the expired one.
   */
  token: string | (() => string);
  /**
   * Connection state, reported to the host so it can say so out loud.
   *
   * Without this a host cannot tell "syncing" from "silently discarding everything": the
   * provider retries a refused upgrade forever with backoff and throws nothing, so an editor
   * whose endpoint is not routed looks identical to one that works until the tab is closed.
   * A host that omits it gets exactly the previous behaviour (slate-feature-transfer W6, the
   * desk.docit.pro `/collaboration` handle).
   */
  onStatus?(status: CollabStatus): void;
}

export interface EditorCoreConfig {
  /** The local human actor: provisional-stamp attribution, commit `by`, insertObject attribution. */
  localParticipant: ParticipantRef;
  /** Existing v2 state (base64 Yjs update) to seed from, when re-opening a document. */
  seedStateBase64?: string;
  /**
   * When present, the engine binds its Y.Doc to the v2 collaboration room `document-v2.<id>`
   * via Hocuspocus. Kept internal so no Yjs type crosses the public API (AC 11.3).
   */
  collab?: CollabConfig;
  /** Empty-doc placeholder text, rendered via a node decoration + CSS. */
  placeholder?: string;
  /** Stable timestamp source; the factory injects one, this default is belt-and-braces only. */
  now?: () => string;
  /**
   * Fires on commit (acceptance), over COMMITTED content only, to precipitate truth/decision
   * cards linked to their source span (AC 10.1/10.3). Defaults to a no-op until a thinker is
   * assigned (OQ-4). Host-injected so the LLM call stays out of the engine.
   */
  deriver?: Deriver;
  /** Called after any content-mutating transaction, with fresh projections for write-through. */
  onChange?(projections: { contentJson: unknown; markdown: string; stateBase64: string }): void;
  /** Called when the synced proposal store changes (drives the review chrome). */
  onProposalsChanged?(proposals: Proposal[]): void;
  /** Called after every dispatched transaction (and once after mount) with the plain selection snapshot. */
  onSelectionChange?(info: SelectionInfo): void;
  /**
   * Fired ONLY by the 'Run with AI' affordance on a live-object card. Edit/done/remove are
   * handled internally by the NodeViews via attributed transactions and never surface here.
   */
  onObjectAction?(req: ObjectActionRequest): void;
  /**
   * Skin-supplied NodeViews merged into the EditorView config (doc-widgets W4). The engine stays
   * framework-free: a React skin renders `widget`/`chart` nodes here without editor-core ever
   * importing React. Engine-owned views (live objects) win on collision — a skin must not be able
   * to silently replace an attributed-transaction surface.
   */
  extraNodeViews?: Record<
    string,
    (node: unknown, view: unknown, getPos: () => number | undefined) => unknown
  >;
}

const PROPOSALS_KEY = "proposals";

/** Monotonic per-session counter for deterministic-enough live-object ids (no Math.random). */
let objectCounter = 0;

export class EditorCore implements EngineHandle {
  private readonly ydoc: Y.Doc;
  private readonly yProposals: Y.Map<Proposal>;
  private readonly provider: HocuspocusProvider | null = null;
  private readonly deriver: Deriver;
  private readonly now: () => string;
  private state: EditorState;
  private view: EditorView | null = null;

  constructor(private readonly config: EditorCoreConfig) {
    this.now = config.now ?? (() => new Date().toISOString());
    if (config.collab) {
      // The provider owns the Y.Doc for the v2 room; the engine binds to it. Real-time sync +
      // server-side persistence (StateV2) come for free from the reused Hocuspocus spine.
      const onStatus = config.collab.onStatus;
      this.provider = new HocuspocusProvider({
        url: config.collab.url,
        name: `document-v2.${config.collab.documentId}`,
        token: config.collab.token,
        ...(onStatus
          ? {
              onStatus: (event: { status: string }) => {
                onStatus(
                  event.status === "connected"
                    ? "connected"
                    : event.status === "connecting"
                      ? "connecting"
                      : "disconnected",
                );
              },
              onDisconnect: () => onStatus("disconnected"),
              onAuthenticationFailed: () => onStatus("unauthorized"),
            }
          : {}),
      });
      // The first status event only arrives once the socket resolves one way or the other;
      // say "connecting" now so a host never has to invent an initial state.
      onStatus?.("connecting");
      this.ydoc = this.provider.document;
    } else {
      this.ydoc = new Y.Doc();
    }
    if (config.seedStateBase64) {
      Y.applyUpdate(this.ydoc, base64ToBytes(config.seedStateBase64));
    }
    this.deriver = config.deriver ?? new NoopDeriver();
    this.yProposals = this.ydoc.getMap(PROPOSALS_KEY);
    this.yProposals.observe(() => this.emitProposals());

    // ── The engine plugin stack. ORDER MATTERS: sync first, then Yjs-aware undo + its keys,
    //    then editing keys (list/commit/format) BEFORE the base keymap so they win, then input
    //    rules, then the passive decorators/stampers. ──
    this.state = EditorState.create({
      schema: editorSchema,
      plugins: [
        ySyncPlugin(this.ydoc.getXmlFragment("default")),
        yUndoPlugin(),
        keymap({ "Mod-z": yUndo, "Mod-y": yRedo, "Mod-Shift-z": yRedo }),
        keymap(this.buildEditingKeymap()),
        keymap(baseKeymap),
        buildInputRules(),
        ...(config.placeholder ? [placeholderPlugin(config.placeholder)] : []),
        provisionalStampPlugin(config.localParticipant, this.now),
      ],
    });
  }

  /** Mount the editor onto a host element (skins call this). Kept internal-typed. */
  mount(host: HTMLElement): void {
    const core = this;
    this.view = new EditorView(host, {
      state: this.state,
      // Plain function on purpose: ProseMirror invokes it with the view as `this`, and
      // y-prosemirror dispatches DURING the EditorView constructor (its plugin view() hook
      // force-rerenders) — before `this.view` is assigned. Routing through the invocation's
      // own view keeps this.state and view.state in lockstep in that window.
      dispatchTransaction(tr) {
        core.dispatchWith(this, tr);
      },
      // Live-object NodeViews (task/decision/truth cards + reminder chip). They mutate ONLY
      // through the narrow host slice below — each change is one attributed act transaction.
      nodeViews: {
        // Skin views first, engine views second: on collision the engine wins (see config docs).
        ...((this.config.extraNodeViews ?? {}) as unknown as Record<string, never>),
        ...buildObjectNodeViews({
          updateObjectAtPos: (pos, patch) => this.updateObjectAtPos(pos, patch),
          removeObjectAtPos: (pos) => this.removeObjectAtPos(pos),
          onObjectAction: (req) => this.config.onObjectAction?.(req),
        }),
      },
    });
    this.emitSelection();
  }

  destroy(): void {
    this.view?.destroy();
    this.view = null;
    // When a provider owns the doc, destroying the provider tears the doc down; otherwise
    // destroy our own standalone doc.
    if (this.provider) {
      this.provider.destroy();
    } else {
      this.ydoc.destroy();
    }
  }

  focus(): void {
    this.view?.focus();
  }

  private dispatch(tr: Transaction): void {
    this.dispatchWith(this.view, tr);
  }

  private dispatchWith(view: EditorView | null, tr: Transaction): void {
    this.state = view ? view.state.apply(tr) : this.state.apply(tr);
    view?.updateState(this.state);
    if (tr.docChanged) {
      this.emitChange();
    }
    // Selection info is reported after EVERY dispatch — content and caret moves alike.
    this.emitSelection();
  }

  // ── Editing commands driven by the chrome (bubble bar / keymap) ──

  /** Yjs-aware undo (y-prosemirror UndoManager — NOT prosemirror-history). */
  undo(): boolean {
    return yUndo(this.state);
  }

  redo(): boolean {
    return yRedo(this.state);
  }

  toggleFormat(format: InlineFormat): void {
    toggleMark(editorSchema.marks[format])(this.state, (tr) => this.dispatch(tr));
  }

  /** Toggle semantics: setting the active block type reverts to paragraph; blockquote wraps/lifts. */
  setBlockType(block: BlockType): void {
    const dispatch = (tr: Transaction) => this.dispatch(tr);
    const current = this.computeSelectionInfo().blockType;
    if (block === "blockquote") {
      if (current === "blockquote") lift(this.state, dispatch);
      else wrapIn(editorSchema.nodes.blockquote)(this.state, dispatch);
      return;
    }
    const target = current === block ? "paragraph" : block;
    if (target === "paragraph" || target === "code_block") {
      pmSetBlockType(editorSchema.nodes[target])(this.state, dispatch);
      return;
    }
    const level = Number(target.slice("heading".length));
    pmSetBlockType(editorSchema.nodes.heading, { level })(this.state, dispatch);
  }

  /** wrapInList when not in that list type; liftListItem when already in it. */
  toggleList(list: ListType): void {
    const dispatch = (tr: Transaction) => this.dispatch(tr);
    if (this.computeSelectionInfo().listType === list) {
      liftListItem(editorSchema.nodes.list_item)(this.state, dispatch);
    } else {
      wrapInList(editorSchema.nodes[list])(this.state, dispatch);
    }
  }

  /** Commit the current selection as the local participant. False when empty/nothing to do. */
  commitSelection(): boolean {
    const { from, to, empty } = this.state.selection;
    if (empty) return false;
    if (this.scanCommitment(from, to) === "committed") return false;
    this.commitRange({ from, to }, this.config.localParticipant.id, this.now());
    return true;
  }

  getSelectionInfo(): SelectionInfo {
    return this.computeSelectionInfo();
  }

  /**
   * The current selection as a writer-back anchor (plain text offsets + context windows), or
   * null when nothing is selected — which is a state, not a failure.
   *
   * `textBetween(…, '\n', '\n')` is used for BOTH the offsets and the context windows, and it
   * is the same call the markdown projection's block separator agrees with: a host that
   * stores `startOffset` here and re-finds the span later gets the same characters back.
   * `contextWindow` is the host's, because the size of "enough context" is a product
   * decision (a document anchor stores 400 either side) and not the engine's to fix.
   */
  getSelectionAnchor(contextWindow: number): SelectionAnchor | null {
    const { doc, selection } = this.state;
    const { from, to, empty, $from } = selection;
    if (empty) return null;

    const before = doc.textBetween(0, from, "\n", "\n");
    const text = doc.textBetween(from, to, "\n", "\n");
    const after = doc.textBetween(to, doc.content.size, "\n", "\n");
    const window = Math.max(0, Math.floor(contextWindow));

    // Nearest ancestor carrying a partId — the selection head's own block when it has one,
    // else the block that contains it (a list item's paragraph inherits the list's id).
    let partId: string | null = null;
    for (let depth = $from.depth; depth >= 0; depth -= 1) {
      const candidate = $from.node(depth).attrs.partId;
      if (typeof candidate === "string" && candidate.length > 0) {
        partId = candidate;
        break;
      }
    }

    return {
      text,
      from,
      to,
      startOffset: before.length,
      endOffset: before.length + text.length,
      contextBefore: window > 0 ? before.slice(-window) : "",
      contextAfter: window > 0 ? after.slice(0, window) : "",
      parentType: $from.parent.type.name,
      partId,
    };
  }

  /**
   * Re-find a span by its TEXT and return the range it occupies now, or null when the text is
   * no longer in the document.
   *
   * This is what a host uses instead of an anchor mark: a surface that stored a quotation
   * (a comment thread, a stored anchor) re-locates it by content, so a stale POSITION can
   * never overwrite the wrong words — if the text changed, the answer is null and the caller
   * refuses rather than writing somewhere plausible. Offsets and positions are related by the
   * same `textBetween(…, '\n', '\n')` projection {@link getSelectionAnchor} uses, so the two
   * agree about where a block ends.
   */
  findText(text: string): DocRange | null {
    const needle = text.trim();
    if (needle.length === 0) return null;
    const { doc } = this.state;
    const flat = doc.textBetween(0, doc.content.size, "\n", "\n");
    const index = flat.indexOf(needle);
    if (index < 0) return null;
    return {
      from: this.positionForOffset(index),
      to: this.positionForOffset(index + needle.length),
    };
  }

  /**
   * Plain-text offset → engine position, by binary search on the SAME `textBetween` call the
   * offsets were produced with. `textBetween(0, p)` grows monotonically with `p`, and reusing
   * the projection rather than walking text nodes is what keeps the two from drifting the
   * first time the schema gains a node.
   */
  private positionForOffset(offset: number): number {
    const { doc } = this.state;
    let low = 0;
    let high = doc.content.size;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (doc.textBetween(0, middle, "\n", "\n").length < offset) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }
    return low;
  }

  // ── EngineHandle: the ONLY content-mutating paths ──

  transactAttributed(
    range: DocRange,
    op: "replace" | "insert" | "delete",
    text: string | undefined,
    attribution: Attribution,
  ): void {
    // NOT wrapped in ydoc.transact({attribution}): a Yjs origin never leaves the process and
    // nothing here reads it — attribution durably travels on the marks below (and the object
    // attrs, for block inserts). The wrapper had one real effect: deferring y-prosemirror's
    // observer past its mutex, which made EVERY engine write rebuild the entire PM document
    // (and remount every NodeView — widgets flickered and lost state on each commit/AI edit).
    {
      let tr = this.state.tr.setMeta(DOCIT_INTERNAL, true);
      const attrMark = editorSchema.marks[ATTRIBUTION_MARK].create({
        participantId: attribution.participant.id,
        participantKind: attribution.participant.kind,
        displayName: attribution.participant.displayName,
        model: attribution.participant.model ?? null,
        lane: attribution.lane,
        ts: attribution.ts,
      });
      // New AI/human text is provisional by default (AC 3.2).
      const commitMark = editorSchema.marks[COMMITMENT_MARK].create({
        state: "provisional" as CommitmentState,
      });

      if (op === "delete") {
        tr = tr.delete(range.from, range.to);
      } else {
        // Multi-paragraph payload (typical AI output): land it as real paragraph nodes, not a
        // literal "\n" run inside one textblock. Whitespace/newline-only payloads yield ZERO
        // paragraphs — fall through to the inline path so a 'replace' never degrades into a
        // silent deletion of the selected range.
        const paragraphs =
          text && text.includes("\n")
            ? text
                .split(/\n+/)
                .map((line) => line.trim())
                .filter((line) => line.length > 0)
                .map((line) =>
                  editorSchema.nodes.paragraph.create(
                    null,
                    editorSchema.text(line, [attrMark, commitMark]),
                  ),
                )
            : [];
        if (paragraphs.length > 0) {
          const to = op === "replace" ? range.to : range.from;
          tr = tr.replaceRange(range.from, to, new Slice(Fragment.from(paragraphs), 0, 0));
        } else {
          const content = text ? editorSchema.text(text, [attrMark, commitMark]) : null;
          if (op === "replace") {
            tr = content
              ? tr.replaceWith(range.from, range.to, content)
              : tr.delete(range.from, range.to);
          } else if (content) {
            tr = tr.insert(range.from, content);
          }
        }
      }
      this.dispatch(tr);
    }
  }

  commitRange(range: DocRange, by: string, ts: string): void {
    // Capture the committed text BEFORE the transaction so the deriver reads exactly what was
    // stood behind (committed content only, AC 10.3).
    const committedText = this.state.doc.textBetween(range.from, range.to, " ", " ");
    {
      const mark = editorSchema.marks[COMMITMENT_MARK].create({
        state: "committed" as CommitmentState,
        by,
        ts,
      });
      const tr = this.state.tr.setMeta(DOCIT_INTERNAL, true).addMark(range.from, range.to, mark);
      this.dispatch(tr);
    }
    // Fire-and-forget: the deriver may call a thinker (async). Failures never block the commit.
    void this.runDeriver(range, committedText, by);
  }

  insertObjectAttributed(
    kind: string,
    summary: string,
    data: Record<string, unknown>,
    attribution: Attribution,
  ): void {
    const def = getLiveObject(kind);
    if (!def) return; // Unknown kind — nothing to insert (registry is the source of truth).
    const nodeType = editorSchema.nodes[def.nodeType];
    if (!nodeType) return;
    objectCounter += 1;
    const node = nodeType.create({
      objectId: `obj-${objectCounter}-${this.now()}`,
      kind,
      summary,
      data,
    });
    {
      let tr = this.state.tr.setMeta(DOCIT_INTERNAL, true);
      const doc = this.state.doc;
      const first = doc.firstChild;
      if (
        doc.childCount === 1 &&
        first &&
        first.type.name === "paragraph" &&
        first.content.size === 0
      ) {
        // Empty doc: the object replaces the single empty paragraph.
        tr = tr.replaceWith(0, doc.content.size, node);
      } else {
        // Insert as a block immediately after the top-level block containing the selection head.
        const { $head } = this.state.selection;
        const pos = $head.depth > 0 ? $head.after(1) : $head.pos;
        tr = tr.insert(pos, node);
      }
      this.dispatch(tr);
    }
  }

  /**
   * Insert a `widget` block (doc-widgets §D6) — same insertion semantics as live objects:
   * one attributed transaction, after the top-level block containing the selection head,
   * replacing the single empty paragraph of an empty doc.
   */
  insertWidgetAttributed(
    data: Record<string, unknown>,
    alt: string,
    attribution: Attribution,
  ): void {
    const nodeType = editorSchema.nodes.widget;
    if (!nodeType) return;
    // Stored as a JSON STRING, not an object: y-prosemirror compares node attrs by identity
    // when syncing into the CRDT, and an object attr (decoded fresh from Yjs on every read)
    // never compares equal — so the full payload would be re-written into stateV2 on every
    // transaction in the doc. Strings compare by value. Every reader already goes through
    // coerceWidgetData, which tolerates both forms.
    const node = nodeType.create({ data: JSON.stringify(data), alt });
    {
      let tr = this.state.tr.setMeta(DOCIT_INTERNAL, true);
      const doc = this.state.doc;
      const first = doc.firstChild;
      if (
        doc.childCount === 1 &&
        first &&
        first.type.name === "paragraph" &&
        first.content.size === 0
      ) {
        tr = tr.replaceWith(0, doc.content.size, node);
      } else {
        const { $head } = this.state.selection;
        const pos = $head.depth > 0 ? $head.after(1) : $head.pos;
        tr = tr.insert(pos, node);
      }
      this.dispatch(tr);
    }
  }

  // ── Live-object mutations (update / done / remove) + insert-point helper ──

  /** Patch a live object's summary/data by objectId. False when no such object exists. */
  updateObject(
    objectId: string,
    patch: { summary?: string; data?: Record<string, unknown> },
  ): boolean {
    const hit = this.findObject(objectId);
    if (!hit) return false;
    this.updateObjectAtPos(hit.pos, patch);
    return true;
  }

  /** Remove a live object by objectId. False when no such object exists. */
  removeObject(objectId: string): boolean {
    const hit = this.findObject(objectId);
    if (!hit) return false;
    this.removeObjectAtPos(hit.pos);
    return true;
  }

  /**
   * Clamp `pos` to [0, doc.content.size] and return a valid insertion point as an empty range —
   * the insert-after target for AI-result flows and the whole-doc prompt mode. Block-level gaps
   * (e.g. right after a trailing live-object card, or the document end) are kept AS-IS:
   * ProseMirror's replace fitter wraps inline payloads into a fresh paragraph there, whereas
   * snapping via `TextSelection.near` would drag the target backward INTO the preceding block
   * (gluing AI results onto the last word, or landing them BEFORE the card they belong after).
   * Only positions already inside a textblock are snapped, as a belt-and-braces guard.
   */
  insertAt(pos: number): DocRange {
    const size = this.state.doc.content.size;
    const clamped = Math.max(0, Math.min(Math.floor(pos), size));
    const $pos = this.state.doc.resolve(clamped);
    if (!$pos.parent.inlineContent) {
      return { from: clamped, to: clamped };
    }
    const snapped = TextSelection.near($pos).from;
    return { from: snapped, to: snapped };
  }

  /**
   * Fresh empty insert range immediately AFTER the live object with `objectId`, re-resolved
   * against the CURRENT doc (positions captured earlier go stale as soon as anyone types).
   * Null when the object no longer exists.
   */
  insertAfterObject(objectId: string): DocRange | null {
    const hit = this.findObject(objectId);
    if (!hit) return null;
    return this.insertAt(hit.pos + hit.node.nodeSize);
  }

  /** ObjectId lookup: linear doc.descendants scan (objects are rare; positions must be fresh). */
  private findObject(objectId: string): { node: PMNode; pos: number } | null {
    let found: { node: PMNode; pos: number } | null = null;
    this.state.doc.descendants((node, pos) => {
      if (found) return false;
      if (node.attrs.objectId === objectId && typeof node.attrs.kind === "string") {
        found = { node, pos };
        return false;
      }
      return true;
    });
    return found;
  }

  /** Update summary/data at `pos` — ONE attributed act transaction (shared with the NodeViews). */
  private updateObjectAtPos(
    pos: number,
    patch: { summary?: string; data?: Record<string, unknown> },
  ): void {
    const node = this.state.doc.nodeAt(pos);
    if (!node || typeof node.attrs.kind !== "string") return; // stale pos — never touch other nodes
    const attrs = {
      ...node.attrs,
      ...(patch.summary !== undefined ? { summary: patch.summary } : {}),
      ...(patch.data !== undefined ? { data: patch.data } : {}),
    };
    const tr = this.state.tr.setMeta(DOCIT_INTERNAL, true).setNodeMarkup(pos, null, attrs);
    this.dispatch(tr);
  }

  /** Delete the live-object node at `pos` — ONE attributed act transaction. */
  private removeObjectAtPos(pos: number): void {
    const node = this.state.doc.nodeAt(pos);
    if (!node || typeof node.attrs.kind !== "string") return;
    const tr = this.state.tr.setMeta(DOCIT_INTERNAL, true).delete(pos, pos + node.nodeSize);
    this.dispatch(tr);
  }

  /**
   * Run the Deriver over a freshly committed span and insert any precipitated truth/decision cards
   * as attributed live-object nodes linked back to the source range (AC 10.1/10.3). Each card goes
   * through the SAME single write path (one attributed Yjs transaction) — no second write path.
   */
  private async runDeriver(source: DocRange, text: string, committedBy: string): Promise<void> {
    if (this.deriver instanceof NoopDeriver) {
      return; // No thinker assigned — nothing to derive (OQ-4 default).
    }
    let cards;
    try {
      cards = await this.deriver.derive({ range: source, text, committedBy });
    } catch {
      return; // A thinker failure must never corrupt or block the document.
    }
    if (!cards.length) return;

    const participant = this.deriver.participant;
    let tr = this.state.tr.setMeta(DOCIT_INTERNAL, true);
    for (const card of cards) {
      const nodeType = card.kind === "truth" ? "truth_card" : "decision_card";
      const node = editorSchema.nodes[nodeType].create({
        kind: card.kind,
        summary: card.statement,
        // Provenance: back-link to the source span + the AI participant that derived it
        // (OQ-9: keyed by thinker id; "no silent AI", AC 5.4).
        data: { source: card.source, derivedBy: participant.id, from: committedBy },
      });
      // Append after the current document content.
      tr = tr.insert(tr.doc.content.size, node);
    }
    this.dispatch(tr);
  }

  addProposal(proposal: Proposal): void {
    this.yProposals.set(proposal.proposalId, proposal);
  }

  setProposalStatus(proposalId: string, status: Proposal["status"]): void {
    const existing = this.yProposals.get(proposalId);
    if (existing) {
      this.yProposals.set(proposalId, { ...existing, status });
    }
  }

  // ── Projections (public read side) ──

  getContentJson(): unknown {
    return toContentV2(this.state.doc);
  }

  getMarkdown(): string {
    return toTextV2(this.state.doc);
  }

  getStateBase64(): string {
    return bytesToBase64(Y.encodeStateAsUpdate(this.ydoc));
  }

  getProposals(): Proposal[] {
    return [...this.yProposals.values()];
  }

  /** Seed an empty v2 doc from a v1 PM-JSON document (copy-forward transform, AC 12.1). */
  seedFromPmJson(pmJson: unknown): void {
    const node = PMNode.fromJSON(editorSchema, pmJson as Record<string, unknown>);
    prosemirrorToYXmlFragment(node, this.ydoc.getXmlFragment("default"));
    this.emitChange();
  }

  // ── Internals ──

  /** Editing keys layered BEFORE baseKeymap so list/commit/format handling wins, then falls through. */
  private buildEditingKeymap(): Record<string, Command> {
    const listItem = editorSchema.nodes.list_item;
    return {
      "Mod-b": toggleMark(editorSchema.marks.strong),
      "Mod-i": toggleMark(editorSchema.marks.em),
      // Same path as the public commitSelection() — Mod-Enter commits the selection.
      "Mod-Enter": () => this.commitSelection(),
      // splitListItem FIRST; returns false outside a list so baseKeymap's Enter takes over.
      Enter: splitListItem(listItem),
      Tab: sinkListItem(listItem),
      "Shift-Tab": liftListItem(listItem),
      // Undo an input-rule transform first (e.g. "# " → heading), else default backspace.
      Backspace: undoInputRule,
    };
  }

  private computeSelectionInfo(): SelectionInfo {
    const { doc, selection, storedMarks } = this.state;
    const { from, to, empty, $from } = selection;

    const text = doc.textBetween(from, to, " ", " ");

    let rect: SelectionInfo["rect"] = null;
    if (this.view) {
      try {
        const start = this.view.coordsAtPos(from);
        const end = this.view.coordsAtPos(to);
        rect = { left: start.left, top: start.top, bottom: end.bottom };
      } catch {
        rect = null; // Unmapped/unmounted position — the chrome hides positional UI.
      }
    }

    const activeFormats: InlineFormat[] = [];
    for (const format of ["strong", "em", "code_inline"] as InlineFormat[]) {
      const markType = editorSchema.marks[format];
      const active = empty
        ? Boolean(markType.isInSet(storedMarks ?? $from.marks()))
        : doc.rangeHasMark(from, to, markType);
      if (active) activeFormats.push(format);
    }

    const parent = $from.parent;
    let blockType: SelectionInfo["blockType"] = "other";
    if (parent.type.name === "heading") {
      const level = parent.attrs.level as number;
      blockType = level >= 1 && level <= 3 ? (`heading${level}` as BlockType) : "other";
    } else if (parent.type.name === "code_block") {
      blockType = "code_block";
    } else if (parent.type.name === "paragraph") {
      blockType = "paragraph";
      for (let depth = $from.depth - 1; depth > 0; depth -= 1) {
        if ($from.node(depth).type.name === "blockquote") {
          blockType = "blockquote";
          break;
        }
      }
    }

    let listType: ListType | null = null;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
      const name = $from.node(depth).type.name;
      if (name === "bullet_list" || name === "ordered_list") {
        listType = name;
        break;
      }
    }

    const commitment = this.scanCommitment(from, to);

    return {
      empty,
      from,
      to,
      text,
      rect,
      activeFormats,
      blockType,
      listType,
      commitment,
      canCommit: !empty && commitment !== "committed",
    };
  }

  /** Fold the commitment mark states of the text in [from, to] into one summary value. */
  private scanCommitment(from: number, to: number): SelectionInfo["commitment"] {
    const commitType = editorSchema.marks[COMMITMENT_MARK];
    let hasCommitted = false;
    let hasProvisional = false;
    let hasUnmarked = false;
    this.state.doc.nodesBetween(from, to, (node) => {
      if (!node.isText) return true;
      const mark = commitType.isInSet(node.marks);
      if (!mark) hasUnmarked = true;
      else if ((mark.attrs.state as CommitmentState) === "committed") hasCommitted = true;
      else hasProvisional = true;
      return false;
    });
    if (!hasCommitted && !hasProvisional) return "none";
    if (hasCommitted && !hasProvisional && !hasUnmarked) return "committed";
    if (hasProvisional && !hasCommitted && !hasUnmarked) return "provisional";
    return "mixed";
  }

  private emitChange(): void {
    const cb = this.config.onChange;
    if (!cb) return;
    // Lazy property getters, same payload shape: these three are FULL-document projections
    // (stateBase64 alone measured 319ms at 20k chars), fired on every content transaction —
    // and the main consumer (EditorV2) reads none of them. Consumers that do read a field
    // pay for exactly that field, when they touch it.
    const core = this as EditorCore;
    cb({
      get contentJson() {
        return core.getContentJson();
      },
      get markdown() {
        return core.getMarkdown();
      },
      get stateBase64() {
        return core.getStateBase64();
      },
    });
  }

  private emitSelection(): void {
    this.config.onSelectionChange?.(this.computeSelectionInfo());
  }

  private emitProposals(): void {
    this.config.onProposalsChanged?.(this.getProposals());
  }
}

function base64ToBytes(b64: string): Uint8Array {
  const bin =
    typeof atob === "function" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa === "function") {
    // Chunked, not char-by-char string concat: a multi-MB state built one += at a time is
    // quadratic allocation. 0x8000 stays safely under engines' argument-count limits.
    const parts: string[] = [];
    for (let i = 0; i < bytes.length; i += 0x8000) {
      parts.push(
        String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000) as unknown as number[]),
      );
    }
    return btoa(parts.join(""));
  }
  return Buffer.from(bytes).toString("base64");
}
